/**
 * Copyright 2025 Google LLC. This software is provided as-is, without warranty 
 * or representation for any use or purpose. Your use of it is subject to your 
 * agreement with Google.
 */
import { AfterViewChecked, Component, inject, ElementRef, ViewChild, ViewChildren, QueryList, HostListener } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { GuidelineService } from '../services/guideline.service';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { PreviewCitationComponent } from '../preview-citation/preview-citation.component';
import { TextFieldModule } from '@angular/cdk/text-field';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PreviewPanelDialogComponent } from '../preview-panel-dialog/preview-panel-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Conversation } from '../conversation';
import { ConversationComponent } from '../conversation/conversation.component';

@Component({
  selector: 'app-guidelines',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, TextFieldModule, MatDialogModule, NgIf, NgFor, MatToolbarModule, PreviewCitationComponent, ConversationComponent],
  providers: [GuidelineService],
  templateUrl: './guidelines.component.html',
  styleUrl: './guidelines.component.scss'
})
export class GuidelinesComponent implements AfterViewChecked {
  isEmptyQueryEntered: boolean = false;
  followUpForm = new FormGroup({
    userInput: new FormControl(),
  });
  chatMsgs: any[] = [];
  starter_questions: string[] = [];
  starter_questions_not_found: any;
  isPreviewPanelLoading: boolean = false;
  enableScroll: boolean = false;
  isMobileView: boolean = false;
  isPreviewVisible: boolean = false;
  isPreviewPanelError: boolean = false;
  dialog = inject(MatDialog);
  starterQuestionsLoader: boolean = false;
  sampleList = [1, 2, 3]
  citationContent: any;
  previewPanelHeight: any;
  @ViewChild('footer') footer!: ElementRef;
  @ViewChild('header') header!: ElementRef;
  @ViewChild('parentContainer') parentContainer!: ElementRef;
  @ViewChild('scrollToHere') scrollToHere!: ElementRef;
  @ViewChildren(ConversationComponent) conversations!: QueryList<ConversationComponent>;

  constructor(private guidelineServ: GuidelineService, public breakpointObserver: BreakpointObserver) {
    this.starterQuestionsLoader = true;
    this.guidelineServ.getStarterQuestions().subscribe({
      next: (response: any) => {
        this.starter_questions = response.starter_questions;
        this.starterQuestionsLoader = false;
      },
      error: (error: any) => {
        this.starter_questions_not_found = error.message
        this.starterQuestionsLoader = false
      }
    })
  }

  ngOnInit() {
    this.breakpointObserver.observe(['(max-width : 768px)']).pipe(distinctUntilChanged())
      .subscribe((result: any) => {
        this.isMobileView = false;
        if (result.matches) {
          this.isMobileView = true;
        } else {
          this.isMobileView = false;
        }
      });
  }

  ngAfterViewChecked() {
    if (this.chatMsgs.length > 1 && this.enableScroll) {
      this.scrollToLatestText();
    }
  }

  scrollToLatestText() {
    const beforeScrollY = window.scrollY; // Get initial scroll position
    this.scrollToHere?.nativeElement.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      const afterScrollY = window.scrollY; // Get new scroll position

      if (beforeScrollY === afterScrollY) {
        this.enableScroll = false;
      }
    }, 500); // Wait for smooth scrolling to complete
  }

  followUp(query: any, event?: any) {
    if (query !== null && query !== undefined && query.trim() !== '') {
      this.isEmptyQueryEntered = false;
      event?.preventDefault(); // Prevent the default Enter behavior

      const conversation: Conversation = {
        userQuestion: query
      };
      this.chatMsgs.push(conversation);
      this.followUpForm.controls['userInput'].setValue(null);
      this.isPreviewVisible = false;
      this.clearCitations();
    } else {
      this.isEmptyQueryEntered = true;
    }
    if (this.chatMsgs.length <= 1) {
      this.scrollTop();
    } else {
      this.enableScroll = true;
    }
  }

  clearCitations(isCitationHighlighted? : any) {
    // for all conversation components call clearCitationHighlight()
    this.conversations.forEach((conversation) => {
      conversation.clearCitationHighlight();
    });
  }
  scrollTop() {
    if (this.parentContainer) {
      this.parentContainer.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    } else {
      // Scroll the entire page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  addQuestionToChat(ques: string) {
    this.followUpForm.controls['userInput'].setValue(ques);
  }

  showCitation(citation: any) {
    this.isPreviewVisible = !citation.isLoading;
    this.isPreviewPanelLoading = citation.isLoading;
    if (!citation.isLoading) {
      if (this.isMobileView) {
        this.openDialog(citation)
      } else {
        this.calculatePanelHeight();
        this.displayPreviewPanel(citation)
      }
    }
  }

  openDialog(citationContent: any) {
    let input = {
      'citationContent': citationContent.content,
      'previewPanelError': citationContent.isError,
      'isMobileView': this.isMobileView
    }
    const dialogRef = this.dialog.open(PreviewPanelDialogComponent, {
      data: input,
      disableClose: true,
      restoreFocus: true,
      width: "90vw",
      maxWidth: '90vw',
      height: "90vh"
    });
    dialogRef.afterClosed().subscribe(result => {
      this.citationContent = null;
    });
  }

  closePreviewSection(isClose: boolean) {
    this.isPreviewVisible = !isClose;
    this.clearCitations()
  }

  displayPreviewPanel(citationContent: any) {
    this.citationContent = citationContent.content;
    this.isPreviewPanelError = citationContent.isError;
  }

  refreshChatMsgs() {
    this.chatMsgs = []
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  calculatePanelHeight() {
    setTimeout(() => {
      const headerRect = this.header?.nativeElement.getBoundingClientRect()
      const footerRect = this.footer?.nativeElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      // -24 represents the gap b/w main section , footer and -40 represents panel should be above 40 px
      this.previewPanelHeight = windowHeight - footerRect.height - headerRect.height -24 - 40;
    })
  }

  ngOnDestroy() {
    this.chatMsgs = [];
    this.breakpointObserver.ngOnDestroy();
  }
}